import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { config } from '../config/aws-config';

const hasConfig = config.userPoolId.length > 0 && config.userPoolClientId.length > 0;
const userPool = hasConfig
  ? new CognitoUserPool({
      UserPoolId: config.userPoolId,
      ClientId: config.userPoolClientId,
    })
  : null;

function getPool(): CognitoUserPool {
  if (!userPool) throw new Error('Cognito is not configured — userPoolId and userPoolClientId are empty in aws-config.ts');
  return userPool;
}

export interface AuthResult {
  success: boolean;
  newPasswordRequired?: boolean;
  cognitoUser?: CognitoUser;
  error?: string;
}

/** Sign in with email + password. Handles NEW_PASSWORD_REQUIRED challenge. */
export function signIn(email: string, password: string): Promise<AuthResult> {
  const user = new CognitoUser({ Username: email, Pool: getPool() });
  const authDetails = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve) => {
    user.authenticateUser(authDetails, {
      onSuccess: () => resolve({ success: true }),
      onFailure: (err) => resolve({ success: false, error: err.message }),
      newPasswordRequired: () => resolve({ success: false, newPasswordRequired: true, cognitoUser: user }),
    });
  });
}

/** Complete the NEW_PASSWORD_REQUIRED challenge. */
export function completeNewPassword(cognitoUser: CognitoUser, newPassword: string): Promise<AuthResult> {
  return new Promise((resolve) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: () => resolve({ success: true }),
      onFailure: (err) => resolve({ success: false, error: err.message }),
    });
  });
}

/** Get the current session (auto-refreshes if needed). Returns null if not signed in. */
export function getCurrentSession(): Promise<CognitoUserSession | null> {
  if (!userPool) return Promise.resolve(null);
  const user = userPool.getCurrentUser();
  if (!user) return Promise.resolve(null);

  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        resolve(null);
      } else {
        resolve(session);
      }
    });
  });
}

/** Get the ID token JWT string for API Authorization header. */
export async function getIdToken(): Promise<string | null> {
  const session = await getCurrentSession();
  return session?.getIdToken().getJwtToken() ?? null;
}

/** Get the current user's email from the ID token. */
export async function getCurrentUserEmail(): Promise<string | null> {
  const session = await getCurrentSession();
  if (!session) return null;
  const payload = session.getIdToken().decodePayload();
  return payload.email ?? null;
}

/** Sign out the current user. */
export function signOut(): void {
  const user = userPool?.getCurrentUser();
  user?.signOut();
}

/** Sign up a new user with email + password. */
export function signUp(email: string, password: string): Promise<AuthResult> {
  return new Promise((resolve) => {
    getPool().signUp(
      email,
      password,
      [new CognitoUserAttribute({ Name: 'email', Value: email })],
      [],
      (err) => {
        if (err) {
          resolve({ success: false, error: err.message });
        } else {
          resolve({ success: true });
        }
      },
    );
  });
}
